import { isNil } from 'lodash';

import { type CalendarDate } from '../dateutils';
import { EntityType, MimeTypes, S3Object, getTranslatedCategory } from '../fileUtils';
import { isNilOrEmpty } from '../objectUtils';
import { CustomLabel, DocumentCategory, FileCategory, SelectedFoldersParamsInput } from '../types';

export const ROOT_FOLDER_ID = 'rootFolder';
export const ROOT_PARENT_FOLDER_ID = 'rootParentFolder';
export const ACCEPTED_FILE_EXTENSION_FOR_SIGNATURE = 'application/pdf';

const FILE_WITHOUT_NAME = 'fileWithoutName';

export enum FolderEntity {
  FILE,
  FOLDER,
  BUILDING,
  UNIT,
  LEASE,
  CONTACT,
  JOINT_CONTACT,
}

export enum FolderBooleanAttributes {
  LOADING_OTHER_CONTEXTS = 'loadingOtherContexts',
  CONTEXT_IS_BUSY = 'contextIsBusy',
  FOLDER_DRAGGING = 'folderDragging',
  FOLDER_ADDING = 'addingFolder',
  SEE_ALL_FOLDERS = 'seeAllFolders',
  INITIAL_LOADING = 'isInitialLoading',
  ON_PAGE = 'onPage',
  SEARCH_FOLDERS = 'searchFolders',
}

export enum DocumentSortOptions {
  ALPHANUMERIC_ASC,
  ALPHANUMERIC_DES,
  SIZE_ASC,
  SIZE_DES,
  DATE_ASC,
  DATE_DES,
}

export enum UniqueNameOptions {
  KEEP_BOTH,
  REPLACE,
}

export enum FolderDownloadOptions {
  LOCAL,
  BACKEND,
}

export enum FolderSortInvoiceOptions {
  PAID,
  UNPAID,
}

export const ROOT_FOLDER_PARAMS: SelectedFoldersParamsInput = {
  translatedName: '',
  folderDepth: '/',
};

export interface DocumentModel extends S3Object {
  hasSignature?: boolean;
  canDrag?: boolean;
  translatedCategory?: string;
  invoicePaid?: boolean;
}

export interface FolderModel {
  id: string;
  folderEntity: FolderEntity;
  size: number;
  updatedAt: string;
  translatedLabel: string;
  documentModel?: DocumentModel;
  documentCategory: DocumentCategoryWithoutReadIdAndClientId;
  folderDepth: string;
  folderDepthByFolderId: string[];
  folderDepthString: string;
  folderDepthStringForDB?: (string | CustomLabel[])[];
  databaseDepthString?: string;
  entityId?: string;
  labelToSortOn?: string;
  parentFolderId: string;
}

export interface ExtendedSearchModel {
  folderEntity?: EntityType;
  folderCategory?: DocumentCategoryWithoutReadIdAndClientId;
  fromDate?: CalendarDate;
  toDate?: CalendarDate;
}

export type DocumentCategoryWithoutReadIdAndClientId = Omit<DocumentCategory, 'readId' | 'clientId'>;

export interface UploadFile {
  fileName: string;
  fileExtension: string;
  progression: number;
}

export interface FileUrlMap {
  url: string;
  fileId: string;
}
export const AllowedFilesEntities = [
  EntityType.BUILDING,
  EntityType.UNIT,
  EntityType.LEASE,
  EntityType.CONTACT,
  EntityType.TECHNIC,
  EntityType.INVOICE,
  EntityType.CHARGE,
];

export const isGeneratedUblDocument = (mimeType?: string | null): boolean => mimeType === MimeTypes.XML;

export const entityStrings = {
  [EntityType.BUILDING.toString()]: 'folders.searchBar.building',
  [EntityType.UNIT.toString()]: 'folders.searchBar.unit',
  [EntityType.LEASE.toString()]: 'folders.searchBar.lease',
  [EntityType.CONTACT.toString()]: 'folders.searchBar.contact',
};

export const uniqueNameStrings = {
  [UniqueNameOptions.KEEP_BOTH]: 'folders.fileActions.keepBoth',
  [UniqueNameOptions.REPLACE]: 'folders.fileActions.rename',
};

/**
 * The one owner category the access sync grants after all the others, so the frontend reads its
 * arrival as the end of the owner's grants. Owners get more categories than this one: the full list is
 * `OWNER_FIXED_FILE_CATEGORIES` in the auth app. Heating is used because every account has it and it
 * cannot be deleted (see `canDelete`).
 */
export const LAST_GRANTED_OWNER_FILE_CATEGORY = FileCategory.HEATING;

export const canDelete = (category: FileCategory) =>
  [
    FileCategory.CUSTOM,
    FileCategory.LEASE_CONTRACT,
    FileCategory.CONTRACT_AMENDMENT,
    FileCategory.PEB,
    FileCategory.RENTAL_DEPOSIT,
    FileCategory.LEASE_CONTRACT_END,
  ].includes(category);

export const canDrag = (category: FileCategory) => {
  if (
    [
      FileCategory.LEASE_CONTRACT,
      FileCategory.LEASE_CONTRACT_END,
      FileCategory.LEASE_INVENTORY_CONTRACT,
      FileCategory.CHARGE_INVOICE,
      FileCategory.INVOICE,
      FileCategory.PEB,
      FileCategory.CO_OWNERSHIP_REGULATIONS,
      FileCategory.RULES_OF_PROCEDURE,
      FileCategory.HYDROCARBONTANK_CERTIFICATE,
      FileCategory.FURNITURE_INVENTORY,
      FileCategory.HEATING,
      FileCategory.CHIMNEY,
      FileCategory.UTILITY,
      FileCategory.DETECTOR,
      FileCategory.INVENTORY_OF_FIXTURES,
      FileCategory.COMMUNICATION,
      FileCategory.CLIENT_LOGO,
      FileCategory.BASIC_DEED,
      FileCategory.STATEMENT_DETAIL,
      FileCategory.STATEMENT_RECEIPT,
      FileCategory.CONTRACT_AMENDMENT,
      FileCategory.LEASE_PRICE_HISTORY_DETAIL,
      FileCategory.ADVERTISEMENT_PICTURE,
      FileCategory.TICKET,
      FileCategory.LETTER,
    ].includes(category)
  ) {
    return false;
  }
  return true;
};

export const canDrop = (category: FileCategory, entity?: EntityType) => {
  const notDroppableCategories = [
    FileCategory.LEASE_CONTRACT_END,
    FileCategory.LEASE_INVENTORY_CONTRACT,
    FileCategory.CHARGE_INVOICE,
    FileCategory.INVOICE,
    FileCategory.PEB,
    FileCategory.CO_OWNERSHIP_REGULATIONS,
    FileCategory.RULES_OF_PROCEDURE,
    FileCategory.HYDROCARBONTANK_CERTIFICATE,
    FileCategory.FURNITURE_INVENTORY,
    FileCategory.HEATING,
    FileCategory.CHIMNEY,
    FileCategory.UTILITY,
    FileCategory.DETECTOR,
    FileCategory.INVENTORY_OF_FIXTURES,
    FileCategory.COMMUNICATION,
    FileCategory.CLIENT_LOGO,
    FileCategory.BASIC_DEED,
    FileCategory.STATEMENT_DETAIL,
    FileCategory.STATEMENT_RECEIPT,
    FileCategory.LEASE_PRICE_HISTORY_DETAIL,
    FileCategory.TICKET,
    FileCategory.LETTER,
  ].includes(category);

  const notDroppableCategoriesForBuilding =
    entity === EntityType.BUILDING &&
    [
      FileCategory.ADVERTISEMENT_PICTURE,
      FileCategory.LEASE_CONTRACT,
      FileCategory.CONTRACT_AMENDMENT,
      FileCategory.RENTAL_DEPOSIT,
      FileCategory.INVOICE_RECEIPT,
    ].includes(category);

  const notDroppableCategoriesForUnit =
    entity === EntityType.UNIT &&
    [
      FileCategory.LEASE_CONTRACT,
      FileCategory.CONTRACT_AMENDMENT,
      FileCategory.RENTAL_DEPOSIT,
      FileCategory.INVOICE_RECEIPT,
    ].includes(category);

  const notDroppableCategoriesForLease =
    entity === EntityType.LEASE && [FileCategory.ADVERTISEMENT_PICTURE].includes(category);

  if (entity === EntityType.CONTACT) {
    return category === FileCategory.CUSTOM;
  }

  return !(
    notDroppableCategories ||
    notDroppableCategoriesForBuilding ||
    notDroppableCategoriesForUnit ||
    notDroppableCategoriesForLease
  );
};

export const getFolderSizeString = (size: number): string => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const checkChildFolders = (
  docs: DocumentModel[],
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  currentCategoryId: string
) => {
  const HasADoc = docs.find((doc) => doc.category?.id === currentCategoryId);
  if (HasADoc) {
    return true;
  }
  const childCategories = categories.filter((category) => category.parentId === currentCategoryId);
  if (!isNilOrEmpty(childCategories)) {
    let hasDoc = false;
    childCategories.forEach((category) => {
      if (checkChildFolders(docs, categories, category?.id ?? '')) {
        hasDoc = true;
        return;
      }
    });
    return hasDoc;
  }
  return false;
};

export const getFolderDepthString = (
  language: string | null,
  folder?: FolderModel,
  previousFolderDepth?: string,
  category?: DocumentCategoryWithoutReadIdAndClientId
): string => {
  if (!isNil(folder)) return `${folder?.folderDepthString}/${folder?.translatedLabel}`.replace('//', '/');
  return `${previousFolderDepth}/${getTranslatedCategory(category!, language)}`.replace('//', '/');
};

const calculateCategoryDepthArray = (
  categoriesDepth: string[],
  categoryId: string,
  categories: DocumentCategoryWithoutReadIdAndClientId[]
): string[] => {
  const categoryDepthArray = categoriesDepth;
  categoryDepthArray.push(categoryId!);

  const childParentCategory = categories.find((category) => category.id === categoryId)?.parentId;
  if (isNil(childParentCategory)) return categoryDepthArray;
  return calculateCategoryDepthArray(categoryDepthArray, childParentCategory, categories);
};

export const getCategoryDeptArray = (
  documentCategory: DocumentCategoryWithoutReadIdAndClientId,
  categories: DocumentCategoryWithoutReadIdAndClientId[]
): string[] => {
  const realCategory = categories.find((category) => category.id === documentCategory.id);
  if (isNil(realCategory)) return [];
  if (isNil(realCategory?.parentId)) return [realCategory.id];

  const depth = calculateCategoryDepthArray([], realCategory.parentId, categories);
  return depth.reverse();
};

export const getCategoriesToWorkWith = (
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  tempCategoryId: string
): DocumentCategoryWithoutReadIdAndClientId[] =>
  categories.map((category) => {
    if (isNil(category.parentId)) {
      return { ...category, parentId: tempCategoryId };
    }
    return category;
  });

export const getMappedS3File = (file: DocumentModel, language: string | null): DocumentModel => {
  const fileCanDrag = canDrag(file.category!.fileCategory!);
  let fileName = '';
  try {
    fileName = decodeURIComponent(file.fileName);
  } catch (_error) {
    fileName = file.fileName;
  }
  return {
    ...file,
    fileName,
    canDrag: fileCanDrag,
    translatedCategory: getTranslatedCategory(file.category!, language),
  };
};

export function getUniqueFileName(folders: string[], fileName: string): string {
  let baseName = fileName;
  let extension = '';
  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex !== -1) {
    baseName = fileName.substring(0, dotIndex);
    extension = fileName.substring(dotIndex);
  }

  let uniqueFileName = fileName;
  let counter = 1;

  while (folders.includes(uniqueFileName)) {
    uniqueFileName = `${baseName}(${counter})${extension}`;
    counter++;
  }

  return uniqueFileName;
}

const compareCategory = (firstCategory: DocumentCategoryWithoutReadIdAndClientId, labels: CustomLabel[]): boolean => {
  let isSame = false;
  firstCategory.labels.some((label, index) => {
    if (label.label === labels[index].label) {
      isSame = true;
      return true;
    }
    return false;
  });
  return isSame;
};

export const getUniqueCategoryNameParameter = (
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  category: CustomLabel[]
): number => {
  let uniqueCategory = category;
  let counter = 0;

  while (categories.find((c) => compareCategory(c, uniqueCategory))) {
    counter++;
    uniqueCategory = category.map((label) => ({ ...label, label: `${label.label} (${counter})` }));
  }

  return counter;
};

export const getAllFilesInFolder = (folders: FolderModel[], selectedFolder: FolderModel): FolderModel[] => {
  const folderDepthString = `${selectedFolder.folderDepth}.${selectedFolder.documentCategory.id}`;
  const files = folders.filter(
    (folder) => folder.folderDepth.startsWith(folderDepthString) && folder.folderEntity === FolderEntity.FILE
  );
  return files;
};

export const getFolderString = (folder: FolderModel, selectedFolder: FolderModel): string => {
  return folder.folderDepthString.replace(selectedFolder.folderDepthString, '');
};

export const addFileToZip = async (
  currentFolder: FolderModel,
  selectedFolder: FolderModel,
  zipObj: Record<string, Uint8Array>,
  urls: FileUrlMap[],
  keepFolderStructure?: boolean
): Promise<void> => {
  if (currentFolder.folderEntity !== FolderEntity.FILE) return;

  const fileUrl = isNilOrEmpty(currentFolder.documentModel?.url)
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      urls.find((url) => url.fileId === currentFolder.documentModel!.id)?.url!
    : currentFolder.documentModel!.url;

  const response = await fetch(fileUrl);
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  const fileName = currentFolder.translatedLabel || FILE_WITHOUT_NAME;
  const folderPath = getFolderString(currentFolder, selectedFolder);
  const folderDepth = keepFolderStructure ? currentFolder.folderDepthString : folderPath;
  zipObj[`${folderDepth}/${fileName}`] = new Uint8Array(arrayBuffer);
};

interface FileNameWithExtension {
  baseName: string;
  extension: string;
}

export const splitFileNameAndExtension = (fileName: string): FileNameWithExtension => {
  const parts = fileName.split('.');
  if (parts.length === 1) return { baseName: fileName, extension: '' };

  const extension = parts.pop();
  return { baseName: parts.join('.'), extension: extension ?? '' };
};

export const sanitizeDepthForZip = (depthString: string): string => {
  // This is done to avoid issues with windows using different path separators
  return depthString.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\//, '');
};

export const createDeterministicFolderId = (
  folderEntity: FolderEntity,
  entityId: string,
  documentCategoryId?: string
): string => {
  const parts = [folderEntity, entityId];
  if (documentCategoryId) {
    parts.push(documentCategoryId);
  }
  return `folder-${parts.join('-')}`;
};
